import React, { useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Link
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Function to convert URLs in text to clickable links
const renderTextWithLinks = (text: string) => {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            color: 'primary.main',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            '&:hover': {
              textDecoration: 'underline',
              opacity: 0.8
            }
          }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const NotificationCenter: React.FC = () => {
  const { announcements, loading, refreshAnnouncements, markAnnouncementsAsRead } = useUser();

  // Refresh announcements on component mount
  useEffect(() => {
    refreshAnnouncements();
  }, [refreshAnnouncements]);

  // Mark announcements as read when component mounts
  useEffect(() => {
    markAnnouncementsAsRead();
  }, [markAnnouncementsAsRead]);

  // Refresh when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      refreshAnnouncements();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshAnnouncements]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CampaignIcon sx={{ fontSize: 32 }} color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          通知中心
        </Typography>
      </Box>

      {announcements.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            目前沒有公告
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            當活動有新公告時，會顯示在這裡
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {announcements.map((announcement) => (
            <Card key={announcement._id} elevation={2}>
              <CardContent>
                {/* Announcement Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {announcement.title}
                      </Typography>
                      {announcement.isImportant && (
                        <Chip label="重要" color="error" size="small" />
                      )}
                      {announcement.visibility === 'private' && (
                        <Chip label="私密" color="secondary" size="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      發布於 {new Date(announcement.createdAt).toLocaleString('zh-TW')}
                    </Typography>
                  </Box>
                </Box>

                {/* Announcement Message */}
                <Alert 
                  severity={announcement.isImportant ? 'error' : 'info'}
                  icon={<CampaignIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {renderTextWithLinks(announcement.message)}
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />

                {/* Event Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    相關活動
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {announcement.event.siteName} - {announcement.event.date} {announcement.event.time}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {announcement.event.location}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default NotificationCenter;
